import { SmartFieldError } from 'components/data-required/smart-fields';
import { Instalment } from 'features/payment/interfaces';

interface InstalmentPlan {
  id: string;
  installments: number;
  installment_amount: string;
  total_amount: string;
}

interface Plan {
  installments: InstalmentPlan[];
  installments_by_bank: boolean;
}

export interface FieldError {
  code: number;
  message: string;
  param: string;
}

interface useSmartFieldProps {
  translation: (key: string) => void;
  locale: string;
  country: string;
  currency: string;
  totalAmount: string;
  onChangeError: (error: SmartFieldError) => void;
  onChangeBrand: (brand: string) => void;
  onChangeInstallments: (installment: Instalment | null) => void;
  onChangeInstallmentsOptions: (installments: Instalment[]) => void;
}

export const useSmartField = ({
  translation,
  locale,
  country,
  currency,
  totalAmount,
  onChangeError,
  onChangeBrand,
  onChangeInstallments,
  onChangeInstallmentsOptions,
}: useSmartFieldProps) => {
  const dlocal = window.dlocal && window.dlocal(process.env.REACT_APP_DLOCAL_KEY);

  const fields = dlocal?.fields({
    locale,
    country,
  });

  const style = {
    base: {
      fontSize: '16px',
      fontFamily: "'Inter UI medium', sans-serif",
      lineHeight: '18px',
      fontSmoothing: 'antialiased',
      fontWeight: '500',
      color: '#32325d',
      '::placeholder': {
        color: '#8a8a8a',
      },
      iconColor: 'rgba(0, 0, 0, 0.87)',
    },
  };

  const panField = fields.create('pan', { style });
  const expirationField = fields.create('expiration', { style });
  const cvvField = fields.create('cvv', { style });

  panField.mount(document.getElementById('pan-field'));
  expirationField.mount(document.getElementById('expiration-field'));
  cvvField.mount(document.getElementById('cvv-field'));

  panField.addEventListener('change', (event: { error: FieldError }) => {
    if (event?.error) {
      onChangeError({ pan: event.error?.message || '' });
    } else {
      onChangeError({ pan: '' });
    }
  });

  expirationField.addEventListener('change', (event: { error: FieldError }) => {
    if (event?.error) {
      onChangeError({ expiration: event.error?.message || '' });
    } else {
      onChangeError({ expiration: '' });
    }
  });

  cvvField.addEventListener('change', (event: { error: FieldError }) => {
    if (event?.error) {
      onChangeError({ cvv: event.error.message || '' });
    } else {
      onChangeError({ cvv: '' });
    }
  });

  const buildInstallments = (installmentsPlan: Plan) => {
    const installments: Instalment[] = [];

    const installmentsByBank = !!installmentsPlan.installments_by_bank;
    installmentsPlan.installments.forEach((plan, idx) => {
      const option: Instalment = {
        id: `${plan.id}`,
        installments: plan.installments,
        installmentAmount: `${plan.installment_amount}`,
        totalAmount: `${plan.total_amount}`,
        installmentsByBank,
        // to select (value & displayName)
        value: `${plan.id}`,
        displayName: installmentsByBank
          ? `${plan.installments} ${
              plan.installments > 1
                ? translation(`smartField.withInterest.prural${country === 'MX' ? '.MX' : ''}`)
                : translation(`smartField.withInterest.singular${country === 'MX' ? '.MX' : ''}`)
            }`
          : `${plan.installments} ${translation('common.of')} ${currency}` +
            ` ${plan.installment_amount} (${translation('common.total')} : ${currency} ${plan.total_amount})`,
      };
      if (idx === 0) {
        onChangeInstallments(option);
      }

      installments.push(option);
    });
    return installments;
  };

  panField.on('brand', (event: { brand: string }) => {
    onChangeError({ pan: '', expiration: '', cvv: '' });
    onChangeInstallmentsOptions([]);
    onChangeInstallments(null);
    if (event.brand) {
      onChangeBrand(event.brand);
      dlocal
        ?.createInstallmentsPlan(panField, totalAmount, currency)
        .then((result: any) => {
          onChangeInstallmentsOptions(buildInstallments(result.installments));
        })
        .catch((result: any) => {
          onChangeInstallmentsOptions([]);
        });
    } else {
      onChangeBrand('');
    }
  });

  return { dlocal, card: panField };
};
